import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatChipsModule } from '@angular/material/chips';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../../../services/auth/auth.service';
import {
  DashboardService,
  DashboardShareLinkListItem,
} from '../../../services/dashboard/dashboard.service';
import { ConfirmResetDialogComponent } from '../duna-dashboard/confirm-reset-dialog.component';
import { SaveOrgTemplateDialogComponent } from '../duna-dashboard/save-org-template-dialog.component';
import { DunaDashboardComponent } from '../duna-dashboard/duna-dashboard.component';
import { environment } from '../../../environment/environment';
import { take } from 'rxjs/operators';

interface OrgTemplateRow {
  id: number;
  name: string;
  description?: string;
  targetRole?: string | null;
  targetRoles?: string[];
  isVisible?: boolean;
  isDefault?: boolean;
  isDunaTemplate?: boolean;
  isOrgTemplate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  widgetCount?: number;
  assignedUserCount?: number;
  shareLinkCount?: number;
}

interface AssignableUser {
  userId: number;
  name: string;
  email: string | null;
}

interface RoleFallbackOverviewRow {
  role: string;
  dunaTemplateName: string | null;
  orgTemplateName: string | null;
  effectiveTemplateName: string | null;
  orgOverridesDuna: boolean;
}

interface UserAssignmentOverviewRow {
  userId: number;
  name: string;
  email: string | null;
  userRole: string;
  assignedTemplateName: string;
  dunaRoleFallbackName: string | null;
  orgRoleFallbackName: string | null;
  effectiveRoleFallbackName: string | null;
  differsFromDunaRoleFallback: boolean;
  differsFromEffectiveRoleFallback: boolean;
}

@Component({
  selector: 'app-duna-dashboard-admin',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    MatSnackBarModule,
    MatDialogModule,
    MatChipsModule,
    DunaDashboardComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './duna-dashboard-admin.component.html',
  styleUrl: './duna-dashboard-admin.component.scss',
})
export class DunaDashboardAdminComponent implements OnInit {
  allowed = false;
  loading = true;
  templates: OrgTemplateRow[] = [];
  shareLinks: DashboardShareLinkListItem[] = [];

  selectedTemplateId: number | null = null;
  assignmentSearch = '';
  assignmentSearchResults: AssignableUser[] = [];
  assignedUsers: AssignableUser[] = [];
  assigning = false;
  savingRole = false;
  isSuperadmin = false;
  selectedTargetRoles: string[] = [];

  newLinkLabel = '';
  newLinkDashboardId: number | null = null;
  creatingLink = false;

  editingLinkFilters: { linkUuid: string; label: string } | null = null;

  templateColumns = ['name', 'type', 'targetRole', 'widgets', 'assigned', 'links', 'actions'];
  linkColumns = ['label', 'dashboard', 'url', 'actions'];
  roleOverviewColumns = ['role', 'duna', 'org', 'effective'];
  userOverviewColumns = [
    'user',
    'userRole',
    'assigned',
    'dunaFallback',
    'orgFallback',
    'deviation',
  ];

  roleFallbackOverview: RoleFallbackOverviewRow[] = [];
  userAssignmentOverview: UserAssignmentOverviewRow[] = [];
  loadingAssignmentOverview = false;

  constructor(
    private authService: AuthService,
    private dashboardService: DashboardService,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.authService
      .getRoles()
      .pipe(take(1))
      .subscribe((roles) => {
        const normalized = roles.map((r) => r.toLowerCase());
        this.allowed =
          normalized.includes('orgadmin') || normalized.includes('superadmin');
        this.isSuperadmin = normalized.includes('superadmin');
        if (!this.allowed) {
          this.loading = false;
          this.cdr.markForCheck();
          return;
        }
        this.reload({ showPageLoading: true });
      });
  }

  /** mat-select may coerce option values to string; keep ids comparable. */
  compareTemplateId = (a: number | string | null, b: number | string | null): boolean =>
    a != null && b != null && Number(a) === Number(b);

  reload(options?: { showPageLoading?: boolean }): void {
    const showPageLoading = options?.showPageLoading ?? false;
    if (showPageLoading) {
      this.loading = true;
    }
    this.dashboardService.getOrgDashboardTemplates().subscribe({
      next: (rows) => {
        this.templates = rows as OrgTemplateRow[];
        this.loading = false;
        if (!this.selectedTemplateId && this.templates.length) {
          this.selectedTemplateId = this.templates[0].id;
        } else if (
          this.selectedTemplateId &&
          !this.templates.some((t) => Number(t.id) === Number(this.selectedTemplateId))
        ) {
          this.selectedTemplateId = this.templates[0]?.id ?? null;
        }
        this.syncSelectedRoleFromTemplate();
        if (this.selectedTemplateId) {
          this.loadAssignments();
        } else {
          this.assignedUsers = [];
        }
        this.loadAssignmentOverview();
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.snackBar.open('Kunne ikke hente dashboards', 'Luk', { duration: 3000 });
        this.cdr.markForCheck();
      },
    });
    this.loadShareLinks();
  }

  loadShareLinks(): void {
    this.dashboardService.getDashboardShareLinks().subscribe({
      next: (res) => {
        this.shareLinks = [...(res.items || [])];
        this.cdr.markForCheck();
      },
      error: () => {
        this.snackBar.open('Kunne ikke hente delingslinks', 'Luk', { duration: 3000 });
        this.cdr.markForCheck();
      },
    });
  }

  editTemplateMeta(template: OrgTemplateRow): void {
    if (!this.canEditTemplateMeta(template)) {
      return;
    }
    this.dialog
      .open(SaveOrgTemplateDialogComponent, {
        width: '480px',
        data: {
          mode: 'edit',
          defaultName: template.name,
          description: template.description ?? '',
          targetRole: template.targetRole ?? null,
          targetRoles: this.targetRolesFromTemplate(template),
          dashboardId: template.id.toString(),
          templateId: template.id.toString(),
        },
      })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) {
          this.snackBar.open('Skabelon opdateret', 'Luk', { duration: 2500 });
          this.reload();
        }
      });
  }

  canEditTemplateMeta(template: OrgTemplateRow): boolean {
    return template.canEdit === true;
  }

  canDeleteTemplate(template: OrgTemplateRow): boolean {
    return template.canDelete === true;
  }

  canSetOrgDefault(template: OrgTemplateRow): boolean {
    return template.isDunaTemplate !== true;
  }

  canSetPlatformDefault(template: OrgTemplateRow): boolean {
    return this.isSuperadmin && template.isDunaTemplate === true;
  }

  defaultStarTitle(template: OrgTemplateRow): string {
    if (template.isDunaTemplate) {
      return template.isDefault
        ? `DUNA-platformstandard for: ${this.templateRolesLabel(template)}`
        : 'Sæt som DUNA-platformstandard (for skabelonens målroller)';
    }
    return template.isDefault
      ? `Organisationsstandard for: ${this.templateRolesLabel(template)}`
      : 'Sæt som organisationsstandard for skabelonens målroller';
  }

  onAdminTabChange(tabIndex: number): void {
    if (tabIndex === 1) {
      this.loadAssignmentOverview();
      if (this.selectedTemplateId) {
        this.loadAssignments();
      }
    }
  }

  loadAssignmentOverview(): void {
    this.loadingAssignmentOverview = true;
    this.dashboardService.getAssignmentOverview().subscribe({
      next: (res) => {
        this.roleFallbackOverview = res.roleFallbacks ?? [];
        this.userAssignmentOverview = res.userAssignments ?? [];
        this.loadingAssignmentOverview = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.roleFallbackOverview = [];
        this.userAssignmentOverview = [];
        this.loadingAssignmentOverview = false;
        this.snackBar.open('Kunne ikke hente tildelingsoversigt', 'Luk', { duration: 3000 });
        this.cdr.markForCheck();
      },
    });
  }

  canToggleVisibility(template: OrgTemplateRow): boolean {
    return template.canEdit === true;
  }

  deleteTemplate(template: OrgTemplateRow): void {
    if (!this.canDeleteTemplate(template)) {
      return;
    }
    const ref = this.dialog.open(ConfirmResetDialogComponent, {
      data: {
        title: 'Slet dashboard-skabelon',
        message: `Er du sikker på, at du vil slette "${template.name}"?`,
        details: [
          'Brugere med skabelonen som valg mister referencen.',
          'Delingslinks til skabelonen kan blive ugyldige.',
        ],
        confirmText: 'Slet',
        confirmColor: 'warn',
      },
    });
    ref.afterClosed().subscribe((ok) => {
      if (!ok) {
        return;
      }
      this.dashboardService.deleteDashboardTemplate(template.id.toString()).subscribe({
        next: () => {
          this.snackBar.open('Skabelon slettet', 'Luk', { duration: 2500 });
          this.reload();
        },
        error: () =>
          this.snackBar.open('Kunne ikke slette', 'Luk', { duration: 3000 }),
      });
    });
  }

  setOrgDefault(template: OrgTemplateRow): void {
    this.http
      .post(`${environment.apiUrl}/dashboard/templates/${template.id}/set-org-default`, {})
      .subscribe({
        next: () => {
          this.snackBar.open('Sat som standard for organisationen', 'Luk', {
            duration: 2500,
          });
          this.reload();
        },
        error: () =>
          this.snackBar.open('Kunne ikke sætte standard', 'Luk', { duration: 3000 }),
      });
  }

  setPlatformDefault(template: OrgTemplateRow): void {
    if (!this.canSetPlatformDefault(template)) {
      return;
    }
    this.http
      .post(`${environment.apiUrl}/dashboard/templates/${template.id}/set-platform-default`, {})
      .subscribe({
        next: () => {
          this.snackBar.open('Sat som DUNA-platformstandard for målrollen', 'Luk', {
            duration: 2500,
          });
          this.reload();
        },
        error: () =>
          this.snackBar.open('Kunne ikke sætte platformstandard', 'Luk', { duration: 3000 }),
      });
  }

  onDefaultStarClick(template: OrgTemplateRow): void {
    if (template.isDunaTemplate) {
      this.setPlatformDefault(template);
    } else {
      this.setOrgDefault(template);
    }
  }

  toggleVisibility(template: OrgTemplateRow): void {
    const next = !(template.isVisible ?? true);
    this.http
      .post(`${environment.apiUrl}/dashboard/templates/${template.id}/set-visibility`, {
        isVisible: next,
      })
      .subscribe({
        next: () => this.reload(),
        error: () =>
          this.snackBar.open('Kunne ikke ændre synlighed', 'Luk', { duration: 3000 }),
      });
  }

  onTemplateSelectionChange(): void {
    this.syncSelectedRoleFromTemplate();
    this.loadAssignments();
  }

  private findTemplateById(id: number | string | null): OrgTemplateRow | undefined {
    if (id == null) {
      return undefined;
    }
    return this.templates.find((t) => Number(t.id) === Number(id));
  }

  private syncSelectedRoleFromTemplate(): void {
    const template = this.findTemplateById(this.selectedTemplateId);
    this.selectedTargetRoles = this.targetRolesFromTemplate(template);
  }

  private targetRolesFromTemplate(template?: OrgTemplateRow): string[] {
    if (!template) {
      return [];
    }
    if (template.targetRoles?.length) {
      return [...template.targetRoles];
    }
    if (template.targetRole) {
      return [template.targetRole];
    }
    return [];
  }

  saveRoleAssignment(): void {
    if (!this.selectedTemplateId) {
      return;
    }
    const template = this.findTemplateById(this.selectedTemplateId);
    if (template?.isDunaTemplate && !this.canEditTemplateMeta(template)) {
      this.snackBar.open('Rolle kan ikke ændres på denne skabelon', 'Luk', {
        duration: 3000,
      });
      return;
    }
    if (!template || !this.canEditTemplateMeta(template)) {
      return;
    }
    this.savingRole = true;
    this.dashboardService
      .updateDashboardTemplate(this.selectedTemplateId.toString(), {
        targetRoles: this.selectedTargetRoles,
      })
      .subscribe({
        next: () => {
          this.savingRole = false;
          this.snackBar.open('Rolle-tildeling gemt', 'Luk', { duration: 2500 });
          this.reload();
          this.cdr.markForCheck();
        },
        error: () => {
          this.savingRole = false;
          this.snackBar.open('Kunne ikke gemme rolle', 'Luk', { duration: 3000 });
          this.cdr.markForCheck();
        },
      });
  }

  get selectedTemplate(): OrgTemplateRow | undefined {
    return this.findTemplateById(this.selectedTemplateId);
  }

  searchUsersForAssignment(): void {
    const term = this.assignmentSearch.trim();
    if (term.length < 2) {
      this.assignmentSearchResults = [];
      this.cdr.markForCheck();
      return;
    }
    this.http
      .get<{ items: AssignableUser[] }>(`${environment.apiUrl}/users`, {
        params: { search: term, limit: '30' },
      })
      .subscribe({
        next: (res) => {
          this.assignmentSearchResults = (res.items || []).map((u) => ({
            userId: (u as any).userId ?? (u as any).id,
            name: (u as any).name,
            email: (u as any).email ?? null,
          }));
          this.cdr.markForCheck();
        },
        error: () => {
          this.assignmentSearchResults = [];
          this.cdr.markForCheck();
        },
      });
  }

  loadAssignments(): void {
    if (!this.selectedTemplateId) {
      this.assignedUsers = [];
      return;
    }
    this.dashboardService
      .getTemplateAssignments(String(this.selectedTemplateId))
      .subscribe({
        next: (res) => {
          const raw = res?.users ?? (res as { items?: AssignableUser[] })?.items ?? [];
          this.assignedUsers = (raw as AssignableUser[]).map((u) => ({
            userId: Number((u as AssignableUser).userId ?? (u as { id?: number }).id),
            name: (u as AssignableUser).name?.trim() || 'Bruger',
            email: (u as AssignableUser).email ?? null,
          }));
          this.cdr.markForCheck();
        },
        error: () => {
          this.assignedUsers = [];
          this.snackBar.open('Kunne ikke hente bruger-tildelinger', 'Luk', { duration: 3000 });
          this.cdr.markForCheck();
        },
      });
  }

  addUserToAssignment(user: AssignableUser): void {
    if (this.assignedUsers.some((u) => u.userId === user.userId)) {
      return;
    }
    this.assignedUsers = [...this.assignedUsers, user];
    this.cdr.markForCheck();
  }

  removeAssignedUser(userId: number): void {
    this.assignedUsers = this.assignedUsers.filter((u) => u.userId !== userId);
    this.cdr.markForCheck();
  }

  saveAssignments(): void {
    if (!this.selectedTemplateId) {
      return;
    }
    this.assigning = true;
    this.dashboardService
      .putTemplateAssignments(
        this.selectedTemplateId.toString(),
        this.assignedUsers.map((u) => u.userId)
      )
      .subscribe({
        next: () => {
          this.assigning = false;
          this.snackBar.open('Tildelinger gemt', 'Luk', { duration: 2500 });
          this.loadAssignmentOverview();
          this.reload();
          this.cdr.markForCheck();
        },
        error: () => {
          this.assigning = false;
          this.snackBar.open('Kunne ikke gemme tildelinger', 'Luk', { duration: 3000 });
          this.cdr.markForCheck();
        },
      });
  }

  createShareLink(): void {
    const dashboardId = this.newLinkDashboardId ?? this.selectedTemplateId;
    if (!dashboardId) {
      this.snackBar.open('Vælg et dashboard', 'Luk', { duration: 2500 });
      return;
    }
    this.creatingLink = true;
    this.dashboardService
      .createDashboardShareLink(dashboardId, this.newLinkLabel)
      .subscribe({
        next: (created) => {
          this.creatingLink = false;
          const template = this.templates.find((t) => Number(t.id) === Number(dashboardId));
          this.shareLinks = [
            {
              linkUuid: created.linkUuid,
              label: created.label ?? this.newLinkLabel,
              dashboardId: created.dashboardId,
              dashboardName: template?.name ?? '',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            ...this.shareLinks,
          ];
          this.newLinkLabel = '';
          this.snackBar.open('Delingslink oprettet', 'Luk', { duration: 2500 });
          this.loadShareLinks();
          this.cdr.markForCheck();
        },
        error: () => {
          this.creatingLink = false;
          this.snackBar.open('Kunne ikke oprette link', 'Luk', { duration: 3000 });
          this.cdr.markForCheck();
        },
      });
  }

  shareUrl(linkUuid: string): string {
    const base = window.location.origin;
    return `${base}/shared-dashboard/${linkUuid}`;
  }

  copyShareUrl(linkUuid: string): void {
    const url = this.shareUrl(linkUuid);
    navigator.clipboard?.writeText(url).then(() => {
      this.snackBar.open('Link kopieret', 'Luk', { duration: 2000 });
    });
  }

  deleteShareLink(row: DashboardShareLinkListItem): void {
    const ref = this.dialog.open(ConfirmResetDialogComponent, {
      data: {
        title: 'Slet delingslink',
        message: `Slet link "${row.label || row.dashboardName}"?`,
        details: [
          'Modtagere kan ikke længere åbne dashboardet via dette link.',
          'Handlingen kan ikke fortrydes.',
        ],
        confirmText: 'Slet',
        confirmColor: 'warn',
      },
    });
    ref.afterClosed().subscribe((ok) => {
      if (!ok) {
        return;
      }
      this.dashboardService.deleteDashboardShareLink(row.linkUuid).subscribe({
        next: () => {
          this.shareLinks = this.shareLinks.filter((l) => l.linkUuid !== row.linkUuid);
          this.snackBar.open('Link slettet', 'Luk', { duration: 2500 });
          this.loadShareLinks();
          this.cdr.markForCheck();
        },
        error: () =>
          this.snackBar.open('Kunne ikke slette link', 'Luk', { duration: 3000 }),
      });
    });
  }

  editLinkFilters(row: DashboardShareLinkListItem): void {
    this.editingLinkFilters = {
      linkUuid: row.linkUuid,
      label: row.label || row.dashboardName,
    };
    this.cdr.markForCheck();
  }

  closeLinkFilterEditor(): void {
    this.editingLinkFilters = null;
    this.cdr.markForCheck();
  }

  roleLabel(role: string | null | undefined): string {
    const map: Record<string, string> = {
      orgadmin: 'Orgadmin',
      uddansvarlig: 'Uddansvarlig',
      orgbruger: 'Orgbruger',
    };
    return role ? map[role] || role : 'Alle roller';
  }

  templateRolesLabel(template: OrgTemplateRow): string {
    const roles = this.targetRolesFromTemplate(template);
    if (!roles.length) {
      return 'Alle roller';
    }
    return roles.map((r) => this.roleLabel(r)).join(', ');
  }
}
